'use client'
import { useMemo } from 'react'
import Section from 'components/Section'
import { _get } from 'utils/shared'
import { DATA_TEST_IDS } from 'constants/test-ids'
import { useMemoizedContentGetter } from 'utils/contentful'
import { Carousel } from 'components/Carousels'
import { MediaImageStandard, Typography } from 'components/Contentful'
import { AchieveTheme, Box, Grid } from '@achieve/ascend'

const FONT_MAP_CLASS = {
  EYEBROW: 'bodySm lg:bodyLg font-bold!',
  TITLE: 'headingLg lg:displayXl font-bold!',
  COPY: 'bodySm lg:bodyLg font-light!',
}

const EmployeeStoriesHero = ({ content }) => {
  const {
    employeeStoriesHeroCopyA,
    employeeStoriesHeroCopyC,
    employeeStoriesHeroEyebrow,
    employeeStoriesHeroImageA,
    employeeStoriesHeroImageC,
    employeeStoriesHeroTitleA,
    employeeStoriesHeroTitleC,
  } = useMemoizedContentGetter(content, [
    'employeeStoriesHeroCopyA',
    'employeeStoriesHeroCopyC',
    'employeeStoriesHeroEyebrow',
    'employeeStoriesHeroImageA',
    'employeeStoriesHeroImageC',
    'employeeStoriesHeroTitleA',
    'employeeStoriesHeroTitleC',
  ])

  // Keep a stable reference so the Carousel doesn't constantly re-init observers on parent re-renders.
  const slides = useMemo(
    () => [
      {
        sys: { id: 'item1' },
        eyebrow: employeeStoriesHeroEyebrow?.textContent,
        title: employeeStoriesHeroTitleA?.textContent,
        copy: employeeStoriesHeroCopyA?.textContent,
        background: _get(employeeStoriesHeroImageA, 'mediaContent', ''),
        alt: _get(
          employeeStoriesHeroImageA,
          'mediaAltText',
          employeeStoriesHeroTitleA?.textContent
        ),
        backgroundMobile: _get(employeeStoriesHeroImageA, 'mobileMediaContent', ''),
        // Avoid visible blink when switching slides (preload hero background images).
        priority: true,
        isMobileUA: content?.isMobileUA,
      },
      {
        sys: { id: 'item3' },
        eyebrow: employeeStoriesHeroEyebrow?.textContent,
        title: employeeStoriesHeroTitleC?.textContent,
        copy: employeeStoriesHeroCopyC?.textContent,
        background: _get(employeeStoriesHeroImageC, 'mediaContent', ''),
        alt: _get(
          employeeStoriesHeroImageC,
          'mediaAltText',
          employeeStoriesHeroTitleC?.textContent
        ),
        backgroundMobile: _get(employeeStoriesHeroImageC, 'mobileMediaContent', ''),
        // Avoid visible blink when switching slides (preload hero background images).
        priority: true,
        isMobileUA: content?.isMobileUA,
      },
    ],
    [
      employeeStoriesHeroCopyA,
      employeeStoriesHeroCopyC,
      employeeStoriesHeroEyebrow,
      employeeStoriesHeroImageA,
      employeeStoriesHeroImageC,
      employeeStoriesHeroTitleA,
      employeeStoriesHeroTitleC,
      content?.isMobileUA,
    ]
  )

  return (
    <Section
      childContainerClassName={'w-full relative m-0 p-0 data-[narrow]:max-w-full!'}
      data-testid="employee-stories-hero-section"
    >
      <EmployeeStoriesCarousel slides={slides} testIDPrefix={DATA_TEST_IDS.EMPLOYEE_STORIES.HERO} />
    </Section>
  )
}

function Slide({ item = {}, selected }) {
  let { alt, background, backgroundMobile, copy, eyebrow, priority, sys, title } = item

  return (
    <Box
      className={
        // Give the `fill` background image a definite height to fill (avoids 0px/auto height issues).
        'min-h-[613px] h-full justify-center transition-opacity duration-1000 ease-[ease-in-out]'
      }
      data-item={sys.id}
    >
      <div className={'w-full min-h-[613px] h-full relative flex items-center justify-center'}>
        <div className="absolute inset-0 bg-top sm:bg-center block lg:hidden">
          <MediaImageStandard
            content={backgroundMobile}
            alt={alt}
            fillRelativeWrapper={false}
            layout="fill"
            objectFit="cover"
            fetchPriority="high"
            preload={priority}
            className="static!"
          />
        </div>
        <div className="absolute inset-0 bg-top sm:bg-center hidden lg:block">
          <MediaImageStandard
            content={background}
            alt={alt}
            fillRelativeWrapper={false}
            layout="fill"
            objectFit="cover"
            fetchPriority="high"
            preload={priority}
            className="static!"
          />
        </div>
        <Grid
          className={'box-border flex-1 h-full max-w-[1280px] relative z-1 pt-8 px-8 lg:pt-[112px]'}
          container
        >
          <Grid
            size={{ xs: 12, lg: 6 }}
            className={
              'pb-[45vh] lg:pl-8 lg:pb-0 [&>*:not(:last-child)]:mb-3 lg:[&>*:not(:last-child)]:mb-[20px]'
            }
            data-testid="employee-stories-hero-text-container"
          >
            <Typography
              content={eyebrow}
              color={AchieveTheme?.ascend?.colors?.blue[500]}
              className={FONT_MAP_CLASS.EYEBROW}
              component={selected ? 'h1' : 'p'}
              data-testid="employee-stories-hero-eyebrow-text"
            />
            <Typography
              content={title}
              className={FONT_MAP_CLASS.TITLE}
              color={AchieveTheme?.ascend?.colors?.neutral?.[900]}
              component="h2"
              data-testid="employee-stories-hero-title-text"
            />
            <Typography
              content={copy}
              className={FONT_MAP_CLASS.COPY}
              color={AchieveTheme?.ascend?.colors?.neutral?.[900]}
              data-testid="employee-stories-hero-copy-text"
              component="p"
            />
          </Grid>
        </Grid>
      </div>
    </Box>
  )
}

function EmployeeStoriesCarousel({ slides }) {
  return (
    <Carousel
      className={'p-0 lg:min-h-[613px] lg:px-0!'}
      carouselItems={slides}
      itemComponent={Slide}
      variant="standard"
      idPrefix={`employee-stories-hero`}
      withIndicators
      classNameIndicators={
        '-mt-12 absolute flex-row [&_div]:opacity-20 [&_div]:flex lg:in-[.slide-text]:-mt-12'
      }
      classNameActiveDot={'opacity-100 bg-blue-500'}
      classNameControls={'h-10! w-10!'}
      autoPlay
      infinityLoop
    />
  )
}

EmployeeStoriesHero.displayName = 'EmployeeStoriesHero'
export { EmployeeStoriesHero }
export default EmployeeStoriesHero
